import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectAmpolCashOrderDetailsContext } from 'selectors/ampolCashOrderDetailsSelectors';
import './ampol-cash-order-details-modal.scss';
import {
  getCashOrderDetails,
  getCashOrderDetailsReset,
  setOrderCardDetailsPageNumber,
} from '../../../../actions/cashActions';
import { DataTable, Icon } from '@ampol/design-system';
import { currencyFormat } from '../../../../utilities/core-utilities';
import { activateMultipleCards } from '../../../../actions/cardActions';
import { DATE_FORMAT_HYPHEN, DATE_FORMAT } from 'utilities/constants';

import dayjs from 'dayjs';
import PageHeader from '../../../../components/layout/page-header/page-header';
import ContentControls from '../../../../components/content-controls/content-controls';
import Status from '../../../../components/status/status';
import ActionMenu from '../../../../components/action-menu/action-menu';
import Validation from 'constants/validation-messages';
import { trackCustomEvent } from 'utilities/app-insights';

type AmpolCashOrderDetailsModalProps = {
  orderNumber: string;
  grandParentAccountNumber: string;
  contractIdentifier: string;
  openCardDetails: (cardDetails: CashOrderCardDetail) => void;
  canReadWrite: boolean;
  selectedOrderDetails: CashOrderSummary;
};

type CashOrderCardDetail = {
  cardNumber: string;
  cardValue: number;
  mediaType: string;
  status: string;
};

type CashOrderSummary = {
  orderDate: string;
  orderNumber: string;
  status: string;
  orderValue: number;
};

const AmpolCashOrderDetailsModal: React.FC<AmpolCashOrderDetailsModalProps> = ({
  orderNumber,
  grandParentAccountNumber,
  contractIdentifier,
  openCardDetails,
  canReadWrite,
  selectedOrderDetails,
}) => {
  const dispatch = useDispatch();
  const {
    assignedGrandfatherClientNumber,
    loggedInUser,
    assignedAccountNumber,
    cashOrderDetailsLoading,
    cashOrderDetailsError,
    cashOrderDetails,
    cashOrderCardDetailsPageNumber,
    currentCashOrderCardDetailsPageNumber,
    activateCardIsLoading,
    activateCardError,
    activateCardSuccess,
  } = useSelector(selectAmpolCashOrderDetailsContext);

  const pageSize = 5;

  const [selection, setSelection] = useState<string[]>([]);

  const totalInactiveCount =
    cashOrderDetails?.result?.filter((x: CashOrderCardDetail) => x.status === 'Inactive').length ?? 0;
  const selectCountOnPage =
    cashOrderDetails?.result?.filter((x: CashOrderCardDetail) => selection.includes(x.cardNumber)).length ?? 0;
  const selectedItemCount = selection.length;
  const showActionMenu = selection.length > 0;
  const selectAll = totalInactiveCount > 0 && totalInactiveCount === selectCountOnPage;

  useEffect(() => {
    return () => {
      dispatch(getCashOrderDetailsReset());
    };
  }, []);

  useEffect(() => {
    if (
      orderNumber &&
      grandParentAccountNumber &&
      contractIdentifier &&
      cashOrderDetails?.orderNumber !== orderNumber
    ) {
      if (cashOrderCardDetailsPageNumber === 1) {
        dispatch(getCashOrderDetails(orderNumber, grandParentAccountNumber, contractIdentifier, 1, pageSize));
      } else {
        dispatch(setOrderCardDetailsPageNumber(1));
      }
    }
  }, [orderNumber, grandParentAccountNumber, contractIdentifier]);

  useEffect(() => {
    if (cashOrderCardDetailsPageNumber !== currentCashOrderCardDetailsPageNumber) {
      dispatch(
        getCashOrderDetails(
          orderNumber,
          grandParentAccountNumber,
          contractIdentifier,
          cashOrderCardDetailsPageNumber,
          pageSize
        )
      );
    }
  }, [cashOrderCardDetailsPageNumber]);

  const onPageNumberChange = (value: number) => {
    setSelection([]);
    dispatch(setOrderCardDetailsPageNumber(value));
    trackCustomEvent(
      'Change Page Number',
      loggedInUser,
      assignedAccountNumber,
      'Clicked to change page of order detail modal data.'
    );
  };

  useEffect(() => {
    if (!activateCardIsLoading && activateCardSuccess) {
      dispatch(
        getCashOrderDetails(
          orderNumber,
          grandParentAccountNumber,
          contractIdentifier,
          cashOrderCardDetailsPageNumber,
          pageSize
        )
      );
    }
  }, [activateCardIsLoading, activateCardError, activateCardSuccess]);

  const handleCardSelectionChange = (card: CashOrderCardDetail) => {
    setSelection((prevSelection) =>
      prevSelection.includes(card.cardNumber)
        ? prevSelection.filter((cardNumber) => cardNumber !== card.cardNumber)
        : [...prevSelection, card.cardNumber]
    );
  };

  const triggerSelectAll = () => {
    setSelection(
      selectAll
        ? []
        : cashOrderDetails.result
            .filter((x: CashOrderCardDetail) => x.status === 'Inactive')
            .map((card: CashOrderCardDetail) => card.cardNumber)
    );
  };

  const handleActionMenuClick = (route: string) => {
    if (route === '/activatecard' && !activateCardIsLoading) {
      dispatch(activateMultipleCards(selection.join(';'), loggedInUser.userId, assignedGrandfatherClientNumber));
      setSelection([]);
      trackCustomEvent(
        'Click Activate Bulk',
        loggedInUser,
        assignedAccountNumber,
        'Click Activate action menu button on Cash Order Detail modal.',
        { selectedOrder: orderNumber }
      );
      window.dataLayer.push({
        event: 'CP_Cash_Order_Details_Activate',
        button_click: true,
        cards_activated: selection.join(';'),
      });
    }
  };

  return (
    <div className='ampol-cash-order-details-modal'>
      <DataTable
        caption={'Ampol Cash Order Details'}
        columns={[
          {
            header: 'Date',
            accessorFn: (x) => dayjs(x.orderDate, DATE_FORMAT_HYPHEN).format(DATE_FORMAT),
          },
          {
            header: 'Order Number',
            accessorKey: 'orderNumber',
          },
          {
            header: 'Status',
            accessorKey: 'status',
          },
          {
            header: 'Order Value',
            accessorFn: (x) => currencyFormat(x.orderValue),
          },
        ]}
        columnTemplate={'1fr 1fr 1fr 1fr'}
        data={[selectedOrderDetails]}
        tableVariant='list'
      />
      <div className='ampol-cash-order-details-modal__card-list-container'>
        <PageHeader title='Card list' />
        <ContentControls
          showPageControls={true}
          pageNumber={cashOrderCardDetailsPageNumber}
          onPageChange={onPageNumberChange}
          selectControl={canReadWrite}
          selectedItemCount={selectedItemCount}
          isSelected={selectAll}
          triggerSelectAll={triggerSelectAll}
          itemCount={cashOrderDetails?.resultSize}
          pageLengths={[pageSize]}
        />
        {showActionMenu && (
          <ActionMenu
            items={[{ displayText: 'Activate', route: '/activatecard' }]}
            clickHandler={handleActionMenuClick}
          />
        )}
        <DataTable
          caption={'Ampol Cash Order Card List'}
          columns={[
            {
              header: 'Card Number',
              accessorKey: 'cardNumber',
            },
            {
              header: 'Denomination',
              accessorFn: (x) => currencyFormat(x.cardValue),
            },
            {
              header: 'Type',
              accessorKey: 'mediaType',
            },
            {
              header: 'Status',
              accessorFn: (x) => <Status value={x.status} />,
            },
            {
              id: 'action',
              accessorFn: (cardDetails) => (
                <>
                  <button
                    className={'ampol-cash-order-details-modal__open-card-details'}
                    onClick={() => openCardDetails(cardDetails)}
                  >
                    <Icon url={'/icons/chevron-right.svg'} />
                  </button>
                </>
              ),
              rtl: true,
            },
          ]}
          isLoading={cashOrderDetailsLoading}
          columnTemplate={'1fr 1fr 1fr 1fr 70px'}
          data={cashOrderDetails?.result}
          tableVariant='list'
          loadingRowCount={5}
          onRowClick={(cardDetails) => openCardDetails(cardDetails)}
          isSelectable={canReadWrite}
          onSelect={handleCardSelectionChange}
          rowSelectionDisabledFn={(row) => row.status !== 'Inactive'}
          rowIdFn={(row) => row.cardNumber}
          rowSelectedFn={(row) => selection.includes(row.cardNumber)}
          noDataMessage={cashOrderDetailsError ? Validation.CONTENT_ERROR : Validation.NO_CONTENT}
        />
      </div>
    </div>
  );
};

export default AmpolCashOrderDetailsModal;
